/**
 * Builds → Suggested: the games whose stack lags their tracking job, and
 * what each needs next (GET /api/v1/opsqa/builds/suggested). A re-processed
 * game gets a new job; once that job finishes its tracking must be
 * ingested and the stack rebuilt, and nothing else on the page says so.
 * Read only: each row opens the game's Layer Stack, where the ingest and
 * the builds are run. Refreshed with the board.
 */
import { useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'
import { relativeTime } from './layersLabModel'

export type SuggestedGame = {
  game_id: number
  game_code: string
  org_code: string
  action: 'ingest' | 'build' | 'waiting'
  reason: string
  job_id: string
  job_finished_at: string | null
  ingested_at: string | null
  built_at: string | null
}

export const ACTION_LABEL: Record<SuggestedGame['action'], string> = {
  ingest: 'Ingest tracking',
  build: 'Build the stack',
  waiting: 'Job running',
}

const ACTION_COLOR: Record<SuggestedGame['action'], string> = {
  ingest: '#f97316', build: '#eab308', waiting: '#6b7280',
}

export function SuggestedBuilds({ onOpen, refreshKey }: {
  onOpen: (gameId: number) => void
  /** changes when the board refreshes: read again with it */
  refreshKey?: number
}) {
  const [games, setGames] = useState<SuggestedGame[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let on = true
    api<{ games: SuggestedGame[] }>('/api/v1/opsqa/builds/suggested')
      .then((d) => { if (on) { setGames(d.games); setError(null) } })
      .catch((e: unknown) => {
        if (on) setError(e instanceof ApiError ? e.message : String(e))
      })
    return () => { on = false }
  }, [refreshKey])

  if (error && !games) return <p className="error">Suggested: {error}</p>
  if (!games) return null
  const todo = games.filter((g) => g.action !== 'waiting').length

  return (
    <section className="panel" data-testid="suggested-builds"
             style={{ marginBottom: 'var(--space-3)' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '14px' }}>
        Suggested
        <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>
          {todo === 0
            ? 'every finished job is ingested and built'
            : `${todo} game${todo === 1 ? '' : 's'} to act on`}
        </span>
      </h3>
      {games.length > 0 && (
        <table className="adm-table" style={{ fontSize: '12px' }}>
          <thead>
            <tr><th>Game</th><th>Next</th><th>Why</th><th>Job finished</th>
              <th>Ingested</th><th>Last built</th></tr>
          </thead>
          <tbody>
            {games.map((g) => (
              <tr key={g.game_id}>
                <td>
                  <a href={`/ops/layer-stack?view=match&game=${g.game_id}`}
                     onClick={(e) => { e.preventDefault(); onOpen(g.game_id) }}>
                    #{g.game_id} {g.game_code}
                  </a>
                  <span className="muted"> · {g.org_code}</span>
                </td>
                <td style={{ color: ACTION_COLOR[g.action], fontWeight: 600 }}>
                  {ACTION_LABEL[g.action]}
                </td>
                <td className="muted">{g.reason}</td>
                <td title={g.job_id}>{relativeTime(g.job_finished_at) ?? '—'}</td>
                <td>{relativeTime(g.ingested_at) ?? '—'}</td>
                <td>{relativeTime(g.built_at) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
